import { useState } from 'react';
import { Badge, Button, Card, CardHeader, IconButton, Input, MenuDropdown } from '../../../../components/ui';
import { ItemPicker } from '../../../../components/common/Pickers/Pickers';
import { cn } from '../../../../utils/cn';
import { jobService } from '../../services/jobService';
import type { Item } from '../../../materials/types/materialTypes';
import type { SupplyBy } from '../../../quotations/types/quotationTypes';
import type { ActionProps } from '../../../../hooks/useAction';
import type { Job } from '../../types/jobTypes';
import type { FormEvent } from 'react';

const LINE_STATUS = { pending: ['Pending', 'grey'], issued: ['Issued', 'blue'], received: ['Received', 'sky'], used: ['Used', 'green'] };

/** Inline "how many?" row for issuing or returning stock. */
function QtyRow({ label, max, unit, onSubmit, onCancel, busy }: { label: string; max: number; unit?: string; onSubmit: (qty: number) => void; onCancel: () => void; busy?: boolean }) {
  const [qty, setQty] = useState(max ? String(max) : '');
  const n = Number(qty);
  return (
    <tr className="bg-brand-50/50">
      <td colSpan={7} className="px-5 py-3">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (n > 0) onSubmit(n);
          }}
        >
          <Input size="sm" label={`${label} quantity${unit ? ` (${unit})` : ''}`} autoFocus inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^\d.]/g, ''))} containerClassName="w-44" />
          <Button size="sm" type="submit" loading={busy} disabled={!(n > 0)}>{label}</Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        </form>
      </td>
    </tr>
  );
}

/**
 * Material list of a job with stock actions.
 * Staff: add line, issue from stock, return to stock, remove. Everyone assigned: mark received / used.
 */
interface MaterialDraft {
  itemId: string | null;
  item: Item | null;
  qty: string;
  supplyBy: SupplyBy;
}
const EMPTY_DRAFT: MaterialDraft = { itemId: null, item: null, qty: '1', supplyBy: 'us' };

export function JobMaterials({ job, isStaff, editable, run, busy, onChange }: ActionProps & { job: Job; isStaff: boolean; editable: boolean; onChange: () => void }) {
  const [open, setOpen] = useState<{ id: string; mode: 'issue' | 'return' } | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<MaterialDraft>(EMPTY_DRAFT);

  const act = async (key: string, fn: () => Promise<unknown>, msg: string) => {
    const res = await run(key, fn, msg);
    if (res) {
      setOpen(null);
      onChange();
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.itemId || !(Number(draft.qty) > 0)) return;
    const res = await run('add-material', () => jobService.addMaterial(job._id, { itemId: draft.itemId as string, qty: Number(draft.qty), supplyBy: draft.supplyBy }), 'Material added');
    if (res) {
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      onChange();
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Materials"
        subtitle="Issue our material from stock; leftovers go back to stock"
        action={isStaff && editable && !adding && <Button size="sm" variant="outline" leftIcon="plus" onClick={() => setAdding(true)}>Add material</Button>}
        className="pb-4"
      />
      {job.materials.length === 0 && !adding ? (
        <p className="border-t border-line px-5 py-8 text-center text-sm text-ink-400">No materials on this job.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-y border-line text-[13px] text-ink-500">
              <tr>
                <th scope="col" className="px-5 py-2.5 font-normal">Material</th>
                <th scope="col" className="px-5 py-2.5 font-normal">Supplied by</th>
                <th scope="col" className="px-5 py-2.5 text-right font-normal">Planned</th>
                <th scope="col" className="px-5 py-2.5 text-right font-normal">Issued</th>
                <th scope="col" className="px-5 py-2.5 text-right font-normal">Returned</th>
                <th scope="col" className="px-5 py-2.5 font-normal">Status</th>
                <th scope="col" className="px-5 py-2.5"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="tabular divide-y divide-line">
              {job.materials.map((m) => {
                const ours = m.supplyBy === 'us';
                const net = (m.issuedQty ?? 0) - (m.returnedQty ?? 0);
                const [label, tone] = LINE_STATUS[m.status] ?? [m.status, 'neutral'];
                const items = [
                  isStaff && ours && m.itemId && { label: 'Issue from stock', icon: 'moneyOut', onClick: () => setOpen({ id: m._id, mode: 'issue' }) },
                  isStaff && ours && net > 0 && { label: 'Return to stock', icon: 'moneyIn', onClick: () => setOpen({ id: m._id, mode: 'return' }) },
                  !ours && m.status !== 'received' && { label: 'Mark received from customer', icon: 'check', onClick: () => act(`mark-${m._id}`, () => jobService.markMaterial(job._id, m._id, 'received'), 'Marked received') },
                  m.status !== 'used' && { label: 'Mark used', icon: 'checkCircle', onClick: () => act(`mark-${m._id}`, () => jobService.markMaterial(job._id, m._id, 'used'), 'Marked used') },
                  isStaff && net <= 0 && { divider: true },
                  isStaff && net <= 0 && { label: 'Remove line', icon: 'trash', danger: true, onClick: () => act(`del-${m._id}`, () => jobService.removeMaterial(job._id, m._id), 'Line removed') },
                ].filter(Boolean);
                return [
                  <tr key={m._id}>
                    <td className="px-5 py-3">
                      <p className="font-medium text-navy-900">{m.name}</p>
                      <p className="text-xs text-ink-400">{[m.brand, m.specification, !m.itemId && ours && 'Not linked to a product'].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="px-5 py-3">{ours ? 'Electroz' : <span className="text-ink-500">Customer</span>}</td>
                    <td className="px-5 py-3 text-right">{m.qty} {m.unit}</td>
                    <td className="px-5 py-3 text-right">{ours ? m.issuedQty ?? 0 : '-'}</td>
                    <td className="px-5 py-3 text-right">{ours ? m.returnedQty ?? 0 : '-'}</td>
                    <td className="px-5 py-3"><Badge tone={tone} dot>{label}</Badge></td>
                    <td className="px-3 py-2 text-right">
                      {editable && items.length > 0 && (
                        <MenuDropdown trigger={<IconButton icon="more" size="sm" label={`Actions for ${m.name}`} loading={busy === `mark-${m._id}` || busy === `del-${m._id}`} />} items={items} />
                      )}
                    </td>
                  </tr>,
                  open?.id === m._id && (
                    <QtyRow
                      key={`${m._id}-qty`}
                      label={open.mode === 'issue' ? 'Issue' : 'Return'}
                      unit={m.unit}
                      max={open.mode === 'issue' ? Math.max(0, m.qty - net) : net}
                      busy={busy === 'qty'}
                      onCancel={() => setOpen(null)}
                      onSubmit={(qty) =>
                        open && act('qty', () => jobService.moveMaterial(job._id, m._id, open.mode, qty), open.mode === 'issue' ? `${qty} ${m.unit ?? ''} issued from stock` : `${qty} ${m.unit ?? ''} returned to stock`)
                      }
                    />
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <form onSubmit={add} className="grid gap-3 border-t border-line bg-navy-50/50 p-5 sm:grid-cols-[minmax(0,1fr)_110px_auto] sm:items-end">
          <ItemPicker type="material" label="Material" value={draft.itemId} selectedLabel={draft.item?.name} onChange={(id, item) => setDraft((d) => ({ ...d, itemId: id, item }))} />
          <Input label={`Qty${draft.item ? ` (${draft.item.unit})` : ''}`} inputMode="decimal" value={draft.qty} onChange={(e) => setDraft((d) => ({ ...d, qty: e.target.value.replace(/[^\d.]/g, '') }))} />
          <div className="flex gap-1 pb-0.5" role="radiogroup" aria-label="Who supplies this">
            {(
              [
                ['us', 'We supply'],
                ['customer', 'Customer'],
              ] as [SupplyBy, string][]
            ).map(([v, lab]) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={draft.supplyBy === v}
                onClick={() => setDraft((d) => ({ ...d, supplyBy: v }))}
                className={cn('h-10 rounded-lg border px-3 text-sm font-medium', draft.supplyBy === v ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line bg-white text-ink-500')}
              >
                {lab}
              </button>
            ))}
          </div>
          <div className="flex gap-2 sm:col-span-3 sm:justify-end">
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button type="submit" leftIcon="plus" loading={busy === 'add-material'} disabled={!draft.itemId}>Add to job</Button>
          </div>
        </form>
      )}
    </Card>
  );
}

export default JobMaterials;
